import { useEffect, useRef, useState } from "react";
import type { LearnerExam } from "../../../packages/content-schema";
import type { Timing } from "../../../packages/question-presentation/timing";
import { api } from "./api";
export type Actor = { id: string; name: string; email: string; role: "owner" | "editor" | "learner" };
export type Student = { id: string | null; name: string; email: string; active: boolean; signedIn?: boolean };
export type Assignment = {
  available?: boolean;
  id: string; examId: string; examTitle: string; exam: LearnerExam | null;
  learnerName: string; learnerEmail: string; learnerId: string;
  timing: Timing; status: string; dueAt: string | null; createdAt: string;
  attemptId: string | null; attemptStatus: string | null;
  teacherReviewed?: boolean;
};
const resources = new Map<string, { data: unknown; expires: number }>();
function freshResource<T>(url: string): T | null {
  const value = resources.get(url);
  if (!value || value.expires <= Date.now()) return null;
  return value.data as T;
}
export function cachedResource<T>(url: string): T | null { return freshResource<T>(url); }
export function cacheResource(url: string, data: unknown, ttlMs = 15000) {
  resources.delete(url); resources.set(url, { data, expires: Date.now() + ttlMs });
  if (resources.size > 30) resources.delete(resources.keys().next().value!);
}
export function clearPortalCache() { resources.clear(); }
if (typeof window !== "undefined") window.addEventListener("sat:portal-invalidated", clearPortalCache);
export type ResourceState<T> = { url: string; data: T | null; error: string; loading: boolean };

/** Keep the last complete response painted while a compatible URL refreshes. */
export function resourceView<T>(state: ResourceState<T>, url: string, cached: T | null): ResourceState<T> {
  if (state.url === url) return state;
  return { url, data: cached ?? state.data, error: "", loading: cached === null };
}

export function useResource<T>(url: string) {
  const [state, setState] = useState<ResourceState<T>>(() => {
    const cached = freshResource<T>(url);
    return { url, data: cached, error: "", loading: cached === null };
  });
  const [revision, setRevision] = useState(0);
  const requestVersion = useRef(0);
  useEffect(() => {
    const cached = freshResource<T>(url);
    if (revision === 0 && cached !== null) {
      let cancelled = false;
      queueMicrotask(() => {
        if (!cancelled) setState((current) => current.url === url && current.data === cached && !current.loading
          ? current
          : { url, data: cached, error: "", loading: false });
      });
      return () => { cancelled = true; };
    }
    const controller = new AbortController();
    const version = ++requestVersion.current;
    void api<T>(url, { signal: controller.signal }).then((data) => {
      if (!controller.signal.aborted && version === requestVersion.current) {
        cacheResource(url, data);
        setState({ url, data, error: "", loading: false });
      }
    }).catch((reason: Error) => {
      if (!controller.signal.aborted && version === requestVersion.current) {
        setState((current) => ({ url, data: current.data, error: reason.message, loading: false }));
      }
    });
    return () => controller.abort();
  }, [url, revision]);
  const current = resourceView(state, url, freshResource<T>(url));
  return { ...current, replace: (data: T) => {
    requestVersion.current += 1;
    cacheResource(url, data);
    setState({ url, data, loading: false, error: "" });
  }, reload: () => {
    requestVersion.current += 1;
    resources.delete(url);
    setState((current) => ({ ...current, loading: true, error: "" }));
    setRevision((value) => value + 1);
  } };
}
export const displayTitle = (title: string) => title.split(" — ")[0];
export const statusLabel = (status: string) => ({ assigned: "Assigned", in_progress: "In progress", completed: "Completed", cancelled: "Cancelled", abandoned: "Abandoned" })[status] ?? status;
export const shortDate = (date: string) => new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric" });
export const formatDueDateTime = (date: string) => new Date(date).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export function useDebouncedValue(value: string, delay = 300) {
  const [settled, setSettled] = useState(value);
  useEffect(() => { const timer = setTimeout(() => setSettled(value), delay); return () => clearTimeout(timer); }, [value, delay]);
  return settled;
}
